import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  accepterCandidat,
  annoncerMission,
  createMission,
  fetchMissionCandidatures,
  fetchMissions,
  postulerMission,
  refuserCandidat,
  rejeterLivraison,
  soumettreLivraison,
  updateMissionStatut,
  validerLivraison,
  MISSION_PLATFORMS,
  type Mission,
  type MissionCandidature,
  type MissionInput,
} from '@/lib/missions';
import { getBalance } from '@/lib/wallet';
import { convertAmount, normalizeCurrency } from '@/lib/currency';
import { TYPE_COMPTE_CLIENT } from '@/lib/accountType';
import MissionCard from '@/pages/missions/components/MissionCard';
import CreateMissionModal from '@/pages/missions/components/CreateMissionModal';
import PostulerModal, { type SocialProfile } from '@/pages/missions/components/PostulerModal';
import MissionDetailModal from '@/pages/missions/components/MissionDetailModal';

// ─────────────────────────────────────────────────────────────
// MISSIONS — marketplace interne de campagnes de visibilité.
// Partagée par le tableau de bord commerçant (/dashboard/missions)
// et la page publique /missions (comptes clients inclus).
// ─────────────────────────────────────────────────────────────

export interface MissionsUser extends SocialProfile {
  id: number;
  name?: string | null;
  nomcommerce?: string | null;
  email?: string | null;
  monaie?: string | null;
  Ville?: string | null;
}

interface Props {
  user: MissionsUser;
  /** Type de compte : le contrôle de solde ne concerne que les commerces. */
  typecompte?: number | null;
  /**
   * Quand ce chemin est fourni (espace marchand), « Gérer » ouvre la page
   * de détail dédiée (`/dashboard/missions/:id`) au lieu d'une modale.
   */
  detailBasePath?: string;
}

/** Seuil de solde portefeuille exigé pour lancer une campagne (en MAD). */
export const SOLDE_MIN_MISSION_MAD = 100;

type Tab = 'campagnes' | 'mes-campagnes' | 'mes-candidatures';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'campagnes', label: 'Campagnes', icon: 'ri-compass-3-line' },
  { id: 'mes-campagnes', label: 'Mes campagnes', icon: 'ri-megaphone-line' },
  { id: 'mes-candidatures', label: 'Mes candidatures', icon: 'ri-send-plane-line' },
];

/** Catégories de mission (type de contenu à publier). */
const CONTENT_CATEGORIES = [
  { key: 'all', label: 'Toutes catégories', icon: 'ri-apps-2-line' },
  { key: 'video', label: 'Vidéo', icon: 'ri-video-line' },
  { key: 'images', label: 'Images', icon: 'ri-image-line' },
  { key: 'post', label: 'Post / texte', icon: 'ri-file-text-line' },
];

export default function MissionsPanel({ user, typecompte, detailBasePath }: Props) {
  const navigate = useNavigate();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [candidatures, setCandidatures] = useState<MissionCandidature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>(Number(typecompte) === TYPE_COMPTE_CLIENT ? 'campagnes' : 'mes-campagnes');
  const [query, setQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [postuler, setPostuler] = useState<Mission | null>(null);
  const [detail, setDetail] = useState<Mission | null>(null);
  const [deliver, setDeliver] = useState<{ mission: Mission; candidature: MissionCandidature } | null>(null);
  const [deliverLien, setDeliverLien] = useState('');
  const [deliverVues, setDeliverVues] = useState('');
  const [deliverBusy, setDeliverBusy] = useState(false);
  const [toast, setToast] = useState('');

  const myId = Number(user.id);
  const authorName = user.nomcommerce || user.name || user.email || 'Un membre Zifek';
  const monaie = user.monaie || 'MAD';

  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 4500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await fetchMissions();
      setMissions(rows);
      const props = await fetchMissionCandidatures(rows.map((m) => m.id));
      setCandidatures(props);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les missions.');
      setMissions([]);
      setCandidatures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const propsOf = useCallback(
    (missionId: number) => candidatures.filter((c) => c.mission_id === missionId),
    [candidatures],
  );

  const myCandidatureOf = useCallback(
    (missionId: number) => candidatures.find((c) => c.mission_id === missionId && c.user_id === myId) || null,
    [candidatures, myId],
  );

  const lists = useMemo(() => {
    const match = (m: Mission) => {
      if (categoryFilter !== 'all' && (m.type_contenu || '') !== categoryFilter) return false;
      if (platformFilter !== 'all' && !(m.plateformes || []).includes(platformFilter)) return false;
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return `${m.titre} ${m.description || ''} ${m.ville || ''} ${m.auteur_nom || ''}`.toLowerCase().includes(q);
    };
    const campagnes = missions
      .filter((m) => {
        // Une mission à laquelle j'ai déjà candidaté reste visible,
        // même si les candidatures ont été fermées entre-temps.
        const alreadyApplied = !!myCandidatureOf(m.id);
        const open = m.statut === 'ouverte' || (alreadyApplied && m.statut !== 'terminee');
        return open && match(m);
      });
    const mine = missions
      .filter((m) => m.user_id === myId)
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    const candidaturesList = missions.filter((m) => !!myCandidatureOf(m.id));
    return { campagnes, mine, candidaturesList };
  }, [missions, myId, platformFilter, categoryFilter, query, myCandidatureOf]);

  const current = tab === 'campagnes'
    ? lists.campagnes
    : tab === 'mes-campagnes'
      ? lists.mine
      : lists.candidaturesList;

  /** Tableau de bord des campagnes : candidats, vues validées et montants dus. */
  const campaignStats = useMemo(() => {
    const mineIds = new Set(lists.mine.map((m) => m.id));
    const rows = candidatures.filter((c) => mineIds.has(c.mission_id));
    const vues = rows.reduce((sum, c) => sum + (Number(c.vues_validees) || 0), 0);
    const duParDevise = new Map<string, number>();
    rows.forEach((c) => {
      const montant = Number(c.montant_du) || 0;
      if (montant <= 0) return;
      const mission = lists.mine.find((m) => m.id === c.mission_id);
      const devise = mission?.monaie || 'MAD';
      duParDevise.set(devise, (duParDevise.get(devise) || 0) + montant);
    });
    return {
      candidatures: rows.length,
      enAttente: rows.filter((c) => c.statut === 'en_attente').length,
      retenus: rows.filter((c) => c.statut === 'acceptee' || c.statut === 'livree' || c.statut === 'validee').length,
      publications: rows.filter((c) => c.statut === 'livree').length,
      vues,
      du: Array.from(duParDevise.entries()),
    };
  }, [candidatures, lists.mine]);

  const handleCreate = async (input: MissionInput) => {
    // 🚦 Un commerce doit disposer d'au moins 100 MAD sur son portefeuille
    // pour lancer une campagne (les comptes clients ne sont pas concernés).
    if (Number(typecompte) !== TYPE_COMPTE_CLIENT) {
      const balance = await getBalance(myId);
      const soldeMad = convertAmount(balance, monaie, 'MAD') ?? balance;
      if (!(balance > 0) || soldeMad < SOLDE_MIN_MISSION_MAD) {
        throw new Error(
          `Solde insuffisant : il faut au moins ${SOLDE_MIN_MISSION_MAD} MAD disponibles sur votre portefeuille pour lancer une mission. `
          + `Votre solde actuel est de ${balance.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${normalizeCurrency(monaie)}. `
          + 'Rechargez votre portefeuille puis réessayez.',
        );
      }
    }
    const created = await createMission(myId, authorName, input);
    setCreateOpen(false);
    setTab('mes-campagnes');
    void load();
    const result = await annoncerMission(created);
    showToast(
      result.notified > 0
        ? `Mission publiée et annoncée à ${result.notified} membre${result.notified > 1 ? 's' : ''}.`
        : 'Mission publiée. Aucun autre membre à notifier pour le moment.',
    );
  };

  const handlePostuler = async (mission: Mission, input: { liens: Record<string, string>; abonnes: Record<string, number>; message: string }) => {
    await postulerMission(mission, { id: myId, nom: authorName, email: user.email || null }, input);
    setPostuler(null);
    // On reste sur la liste : la mission continue de s'afficher avec son statut.
    // Le détail (/mon-compte/missions/:id) permet de déposer les liens de publication.
    showToast('Candidature envoyée. Cliquez sur la mission pour suivre et déposer vos liens.');
    await load();
  };

  const openDeliver = (mission: Mission) => {
    const mine = myCandidatureOf(mission.id);
    if (!mine) return;
    setDeliver({ mission, candidature: mine });
    setDeliverLien(mine.lien_publication || '');
    setDeliverVues(mine.vues_declarees ? String(mine.vues_declarees) : '');
  };

  const submitDeliver = async () => {
    if (!deliver) return;
    if (!deliverLien.trim()) {
      showToast('Ajoutez le lien de votre publication.');
      return;
    }
    setDeliverBusy(true);
    try {
      await soumettreLivraison(deliver.mission, deliver.candidature, deliverLien, Number(deliverVues) || 0);
      setDeliver(null);
      showToast('Publication envoyée. Le propriétaire va vérifier les vues.');
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Envoi impossible.');
    } finally {
      setDeliverBusy(false);
    }
  };

  const detailCandidatures = detail ? propsOf(detail.id) : [];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Missions</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Campagnes de visibilité : publiez un contenu, fixez un tarif pour 1000 vues, choisissez vos créateurs.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors w-fit"
        >
          <i className="ri-add-line"></i>
          Créer une mission
        </button>
      </div>

      {Number(typecompte) !== TYPE_COMPTE_CLIENT && (
        <div className="mb-4 flex items-start gap-2 px-3.5 py-2.5 rounded-lg bg-secondary-50 border border-secondary-200/70 text-[12px] text-secondary-900">
          <i className="ri-wallet-3-line mt-0.5"></i>
          <span>
            Pour lancer une campagne, votre portefeuille doit contenir au moins{' '}
            <strong>{SOLDE_MIN_MISSION_MAD} MAD</strong>.{' '}
            <Link to="/dashboard/wallet" className="underline font-medium whitespace-nowrap">Recharger</Link>
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1 p-1 bg-background-100 border border-background-200/60 rounded-full w-fit mb-4">
        {TABS.map((item) => {
          const count = item.id === 'campagnes'
            ? lists.campagnes.length
            : item.id === 'mes-campagnes'
              ? lists.mine.length
              : lists.candidaturesList.length;
          return (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                tab === item.id ? 'bg-background-50 text-foreground-950' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className={item.icon}></i>
              {item.label}
              <span className={tab === item.id ? 'text-foreground-500' : 'text-foreground-400'}>{count}</span>
            </button>
          );
        })}
      </div>

      {tab === 'mes-campagnes' && lists.mine.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-3.5">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
              <i className="ri-eye-line"></i>Vues validées
            </div>
            <p className="mt-1 text-xl font-bold font-heading text-foreground-950">{campaignStats.vues.toLocaleString('fr-FR')}</p>
          </div>
          <div className="rounded-lg border border-accent-200/70 bg-accent-50 p-3.5">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-accent-800">
              <i className="ri-hand-coin-line"></i>Montants à payer
            </div>
            <p className="mt-1 text-base font-bold font-heading text-foreground-950 leading-tight">
              {campaignStats.du.length === 0
                ? '0'
                : campaignStats.du.map(([devise, montant]) => `${montant.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${devise}`).join(' · ')}
            </p>
          </div>
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-3.5">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
              <i className="ri-user-follow-line"></i>Candidats retenus
            </div>
            <p className="mt-1 text-xl font-bold font-heading text-foreground-950">
              {campaignStats.retenus}<span className="text-xs font-medium text-foreground-500"> / {campaignStats.candidatures}</span>
            </p>
          </div>
          <div className="rounded-lg border border-secondary-200/70 bg-secondary-50 p-3.5">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-secondary-900">
              <i className="ri-upload-cloud-2-line"></i>À vérifier
            </div>
            <p className="mt-1 text-xl font-bold font-heading text-foreground-950">{campaignStats.publications}</p>
            {campaignStats.enAttente > 0 && (
              <p className="text-[11px] text-foreground-500 mt-0.5">{campaignStats.enAttente} candidature(s) en attente</p>
            )}
          </div>
        </div>
      )}

      {tab === 'campagnes' && (
        <div className="flex flex-col gap-3 mb-5">
          {/* Filtre par catégorie (type de contenu demandé) */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground-400 mr-1">Catégorie</span>
            {CONTENT_CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCategoryFilter(c.key)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                  categoryFilter === c.key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                <i className={c.icon}></i>
                {c.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => setPlatformFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                platformFilter === 'all' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
              }`}
            >
              Tous les réseaux
            </button>
            {MISSION_PLATFORMS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPlatformFilter(p.key)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                  platformFilter === p.key ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                <i className={p.icon}></i>
                {p.label}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher une mission, une ville…"
              className="w-full h-11 pl-9 pr-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
          </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span className="flex-1">{error}</span>
          <button onClick={() => void load()} className="underline font-medium whitespace-nowrap cursor-pointer">Réessayer</button>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          <p className="text-xs text-foreground-400 mt-3">Chargement des missions…</p>
        </div>
      ) : current.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/60 rounded-lg px-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-megaphone-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-semibold text-foreground-800">
            {tab === 'campagnes' ? 'Aucune campagne disponible' : tab === 'mes-campagnes' ? 'Vous n’avez publié aucune mission' : 'Aucune candidature pour le moment'}
          </p>
          <p className="text-xs text-foreground-500 mt-1 max-w-md">
            {tab === 'campagnes'
              ? 'Les missions publiées par les autres commerces apparaîtront ici.'
              : tab === 'mes-campagnes'
                ? 'Créez une mission pour faire partager votre contenu par la communauté.'
                : 'Postulez à une campagne : vous recevrez une notification si vous êtes retenu.'}
          </p>
          {tab !== 'campagnes' && (
            <button
              onClick={() => setCreateOpen(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-add-line"></i>
              Créer une mission
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {current.map((mission) => {
            const mine = mission.user_id === myId;
            const myCand = myCandidatureOf(mission.id);
            return (
              <MissionCard
                key={mission.id}
                mission={mission}
                isMine={mine}
                myCandidature={myCand}
                candidaturesCount={propsOf(mission.id).length}
                onPostuler={() => setPostuler(mission)}
                onManage={() => (detailBasePath ? navigate(`${detailBasePath}/${mission.id}`) : setDetail(mission))}
                onDeliver={() => (detailBasePath ? navigate(`${detailBasePath}/${mission.id}`) : openDeliver(mission))}
              />
            );
          })}
        </div>
      )}

      {createOpen && (
        <CreateMissionModal
          monaie={monaie}
          ville={user.Ville || ''}
          onClose={() => setCreateOpen(false)}
          onSubmit={handleCreate}
        />
      )}

      {postuler && (
        <PostulerModal
          mission={postuler}
          profile={user}
          onClose={() => setPostuler(null)}
          onSubmit={(input) => handlePostuler(postuler, input)}
        />
      )}

      {detail && (
        <MissionDetailModal
          mission={detail}
          candidatures={detailCandidatures}
          onClose={() => setDetail(null)}
          onFermer={async () => {
            await updateMissionStatut(detail.id, 'fermee');
            showToast('Candidatures fermées : vous pouvez maintenant retenir vos créateurs.');
            await load();
          }}
          onAccepter={async (c) => {
            await accepterCandidat(detail, c);
            showToast('Candidat retenu : il a été notifié pour publier le contenu.');
            await load();
          }}
          onRefuser={async (c) => {
            await refuserCandidat(detail, c);
            await load();
          }}
          onValider={async (c, vues) => {
            const montant = await validerLivraison(detail, c, vues);
            showToast(`Publication validée : ${montant.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${detail.monaie || 'MAD'} dus au créateur.`);
            await load();
          }}
          onRejeter={async (c, motif) => {
            await rejeterLivraison(detail, c, motif);
            showToast('Publication rejetée, le créateur a été prévenu.');
            await load();
          }}
        />
      )}

      {deliver && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeliver(null)} />
          <div className="relative w-full max-w-md bg-background-50 rounded-xl border border-background-200/70">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200/70">
              <div>
                <h3 className="text-base font-bold font-heading text-foreground-950">Ma publication</h3>
                <p className="text-xs text-foreground-500 mt-0.5 truncate">{deliver.mission.titre}</p>
              </div>
              <button onClick={() => setDeliver(null)} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Lien de la publication <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={deliverLien}
                  onChange={(e) => setDeliverLien(e.target.value)}
                  placeholder="https://www.tiktok.com/@vous/video/…"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nombre de vues constatées</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={deliverVues}
                  onChange={(e) => setDeliverVues(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="ex. 12500"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
                <p className="text-[11px] text-foreground-400 mt-1">
                  Le propriétaire vérifiera et validera le montant selon les vues réelles.
                </p>
              </div>
            </div>
            <div className="flex gap-3 px-5 py-4 border-t border-background-200/70">
              <button onClick={() => setDeliver(null)} className="flex-1 px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors">
                Annuler
              </button>
              <button
                onClick={submitDeliver}
                disabled={deliverBusy}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                {deliverBusy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[60] w-[calc(100vw-2rem)] max-w-md">
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-foreground-900 text-background-50 text-xs font-medium">
            <i className="ri-information-line mt-0.5"></i>
            <span className="flex-1">{toast}</span>
          </div>
        </div>
      )}
    </div>
  );
}